import { AbstractControl, UntypedFormBuilder } from '@angular/forms';
import { Meta } from '@appbolaget/aware-model';

export class Helpers {
    /**
     * Extends a class with another class basically.
     * Mainly used for applying meta interface to models
     *
     * @param {Class} derivedCtor
     * @param {Class} baseCtors
     *
     * @return void
     */
    static applyMixins(derivedCtor: any, baseCtors: any[]) {
        baseCtors.forEach((baseCtor) => {
            Object.getOwnPropertyNames(baseCtor.prototype).forEach((name) => {
                derivedCtor.prototype[name] = baseCtor.prototype[name];
            });
        });
    }

    /**
     * Get property adds support for fetching a value
     * from an array using dot notation.
     *
     * Example: getProperty('something.a.few.levels.down', myArray);
     *
     * @param  string property
     * @param  object object
     *
     * @return mixed
     */
    static getProperty(property, object) {
        let parts = property.split('.');

        property = object || this;

        for (let i = 0; i < parts.length; i++) {
            if (!property[parts[i]]) {
                return null;
            }

            property = property[parts[i]];
        }

        return property;
    }

    /**
     * Build reactive form group for meta item
     *
     * Example: buildFormGroupForMeta(fb, 'email', {value: email@example.com });
     *
     * @param {Formbuilder} fb
     * @param {String}      key
     * @param {Object}      meta
     * @param {Boolean}     boolean
     *
     * @return FormGroup
     */
    static buildFormGroupForMeta(fb: UntypedFormBuilder, key, meta, boolean: boolean = false, options: any = false) {
        let value = meta.value;

        if (boolean) {
            value = Helpers.convertToBoolean(meta.value);
        }

        return fb.group(
            {
                id: meta.id,
                resource_id: meta.resource_id,
                key: meta.key || key,
                inherit: meta.inherit,
                inherited_id: meta.inherited_id,
                unique: meta.unique,
                value: value,
                delete: meta.delete || false,
            },
            options ? options : null,
        );
    }

    /**
     * Takes a class instance and clones it
     * @param instance Class instance to clone
     */
    static clone<T>(instance: T): T {
        const copy = new (instance.constructor as { new (): T })();
        Object.assign(copy, instance);
        return copy;
    }

    /**
     * Converts value to boolean
     *
     * @param {any} value
     *
     * @return boolean
     */
    static convertToBoolean(value) {
        if (!value || value === undefined || value === null) {
            return false;
        }

        switch (value) {
            case 'false':
            case 'no':
            case '0':
            case '':
                return false;
            default:
                return true;
        }
    }

    /**
     * Encodes form data for http requests
     * @param object obj
     *
     * @return string
     */
    static encodeElement(obj) {
        let query = '',
            name,
            value,
            fullSubName,
            subName,
            subValue,
            innerObj,
            i;

        for (name in obj) {
            value = obj[name];

            if (value instanceof Array) {
                for (i = 0; i < value.length; ++i) {
                    subValue = value[i];
                    fullSubName = name + '[' + i + ']';
                    innerObj = {};
                    innerObj[fullSubName] = subValue;
                    query += Helpers.encodeElement(innerObj) + '&';
                }
            } else if (value instanceof Object) {
                for (subName in value) {
                    subValue = value[subName];
                    fullSubName = name + '[' + subName + ']';
                    innerObj = {};
                    innerObj[fullSubName] = subValue;
                    query += Helpers.encodeElement(innerObj) + '&';
                }
            } else if (value && value !== null) {
                query += encodeURIComponent(name) + '=' + encodeURIComponent(value) + '&';
            }
        }

        return query.length ? query.substr(0, query.length - 1) : query;
    }

    /**
     * Recursive deep search through object
     * @param o Object of recursive search
     * @param prop Property so search by
     * @param val Value to search by
     * @param retprop Optional property name to retur by instead of object
     */
    static findByProp(o, prop, val, retprop?) {
        if (o == null) {
            return false;
        }

        if (o[prop] === val) {
            return retprop ? o[retprop] : o;
        }

        let result, p;

        for (p in o) {
            if (o.hasOwnProperty(p) && typeof o[p] === 'object') {
                result = this.findByProp(o[p], prop, val);

                if (result) {
                    return retprop ? result[retprop] : result;
                }
            }
        }
        return retprop ? result[retprop] : result;
    }

    static findRecursively(obj, items) {
        for (let i = 0; i < items.length; i += 1) {
            if (items[i].uuid === obj.uuid) {
                return true;
            }

            if (items[i].children && this.findRecursively(obj, items[i].children)) {
                return true;
            }
        }

        return false;
    }

    static getDeepFormGroup<T extends AbstractControl>(base: AbstractControl, ...groups: string[]): T {
        if (!groups.length) {
            return base as T;
        }

        return groups.reduce((acc, curr) => {
            return acc.get(curr) as T;
        }, base as T);
    }

    static getRandomString() {
        let result = '';
        const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

        for (let i = chars.length; i > 0; --i) {
            result += chars[Math.floor(Math.random() * chars.length)];
        }

        return result;
    }

    static getParentArray(model, array = []) {
        if (!model || !model.parent) {
            return array;
        }

        array.push(model.parent);

        return Helpers.getParentArray(model.parent, array);
    }

    static humanFileSize(bytes, si: boolean = false) {
        const thresh = si ? 1000 : 1024;

        if (Math.abs(bytes) < thresh) {
            return bytes + ' B';
        }

        const units = si ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

        let u = -1;

        do {
            bytes /= thresh;
            ++u;
        } while (Math.abs(bytes) >= thresh && u < units.length - 1);

        return bytes.toFixed(1) + ' ' + units[u];
    }

    /**
     * Check to see if value is valid json
     * @param {any} item
     */
    static isJson(item) {
        item = typeof item !== 'string' ? JSON.stringify(item) : item;

        try {
            item = JSON.parse(item);
        } catch (e) {
            return false;
        }

        if (typeof item === 'object' && item !== null) {
            return true;
        }

        return false;
    }

    /**
     * Check to see if value is object
     * @param item
     */
    static isObject(item) {
        return item && typeof item === 'object' && !Array.isArray(item);
    }

    /**
     * Check to see if string is a valid UUID
     * @param {string} uuid
     */
    static isValidUUID(uuid) {
        return /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/.test(uuid);
    }

    /**
     * Do a deep object merge
     * @param target Target object
     * @param sources Merge sources
     */
    static mergeDeep(target, ...sources) {
        if (!sources.length) return target;
        const source = sources.shift();

        if (Helpers.isObject(target) && Helpers.isObject(source)) {
            for (const key in source) {
                if (Helpers.isObject(source[key])) {
                    if (!target[key]) Object.assign(target, { [key]: {} });
                    Helpers.mergeDeep(target[key], source[key]);
                } else {
                    Object.assign(target, { [key]: source[key] });
                }
            }
        }

        return Helpers.mergeDeep(target, ...sources);
    }

    static openPopupWindow(url: string, title: string, w: number, h: number): Window {
        // Fixes dual-screen position                             Most browsers      Firefox
        const dualScreenLeft = window.screenLeft !== undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !== undefined ? window.screenTop : window.screenY;

        const width = window.innerWidth
            ? window.innerWidth
            : document.documentElement.clientWidth
            ? document.documentElement.clientWidth
            : screen.width;
        const height = window.innerHeight
            ? window.innerHeight
            : document.documentElement.clientHeight
            ? document.documentElement.clientHeight
            : screen.height;

        const systemZoom = width / window.screen.availWidth;
        const left = (width - w) / 2 / systemZoom + dualScreenLeft;
        const top = (height - h) / 2 / systemZoom + dualScreenTop;
        const newWindow = window.open(
            url,
            title,
            `
          scrollbars=yes,
          width=${w / systemZoom},
          height=${h / systemZoom},
          top=${top},
          left=${left}
          `
        );

        if (window.focus) newWindow.focus();

        return newWindow;
    }

    /**
     * Create URL params from object
     * @param data Object
     */
    static paramify(data) {
        return Object.entries(data)
            .map(([key, val]) => `${key}=${val}`)
            .join('&');
    }

    /**
     * Convert meta to array if object
     *
     * @param  {any} meta
     * @return {Array<any>}
     */
    static prepareMetaForRequest(meta: any): Array<any> {
        if (Array.isArray(meta)) {
            return Helpers.stripMeta(meta);
        }

        return Helpers.stripMeta(Object.keys(meta).map((k) => meta[k]));
    }

    /**
     * Generate an array of numbers
     * @param {int} start
     * @param {int} end
     */
    static range(start, end) {
        return Array.from({ length: end - start }, (_, k) => k + start);
    }

    /**
     * Reorders array
     * @param event new and old index
     * @param originalArray Array to reorder
     * @param updatePosition Flag to update position property
     */
    static reorderArray(event: { newIndex: number; oldIndex: number }, originalArray: any[], updatePosition = false) {
        const movedItem = originalArray.find((_, index) => index === event.oldIndex);
        const remainingItems = originalArray.filter((_, index) => index !== event.oldIndex);

        let reorderedItems = [...remainingItems.slice(0, event.newIndex), movedItem, ...remainingItems.slice(event.newIndex)];

        if (updatePosition) {
            reorderedItems = reorderedItems.map((item, index) => {
                item.position = index;
                return item;
            });
        }

        return reorderedItems;
    }

    /**
     * Sets property using dot notation
     * @param {string} property
     * @param {any} value
     * @param {Object} object
     *
     * @return mixed
     */
    static setProperty(property, value, object) {
        property = property.split('.');

        for (let i = 0; i < property.length - 1; i++) {
            object = object[property[i]];
        }

        object[property[property.length - 1]] = value;

        return object;
    }

    static slugify(str: string): string {
        str = str.replace(/^\s+|\s+$/g, ''); // trim
        str = str.toLowerCase();

        // remove accents, swap ñ for n, etc
        const from = 'ãàáäåâẽèéëêìíïîõòóöôùúüûñç·/_,:;';
        const to = 'aaaaaeeeeeiiiiooooouuuunc------';

        for (let i = 0, l = from.length; i < l; i++) {
            str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
        }

        str = str
            .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
            .replace(/\s+/g, '-') // collapse whitespace and replace by -
            .replace(/-+/g, '-'); // collapse dashes

        return str;
    }

    static stripMeta(meta) {
        let result = [];

        for (let val of meta) {
            let obj = new Meta(val);
            result.push(Meta.prepare(Meta.fields, obj));
        }

        return result;
    }

    static urlToFile(url, filename: string = 'image.jpg', mimeType) {
        return fetch(url)
            .then(function (res) {
                return res.arrayBuffer();
            })
            .then(function (buf) {
                return new File([buf], filename, { type: mimeType });
            });
    }
}
